import { useState, useEffect } from 'react';
import WizardModal, { WizardButton, WizardLabel, WizardInput, WizardSelect, WizardError } from './WizardModal';
import useWizardSubmit from './useWizardSubmit';
import { get, del } from '../../api';

interface PlanningRule {
  id: number;
  category: string;
  rule: string;
  priority: string;
}

const CATEGORY_OPTIONS = [
  { value: 'Kalorien & Makros', label: 'Kalorien & Makros' },
  { value: 'Meal Prep', label: 'Meal Prep' },
  { value: 'Einkauf & Zutaten', label: 'Einkauf & Zutaten' },
  { value: 'Abwechslung', label: 'Abwechslung' },
  { value: 'Zeitaufwand', label: 'Zeitaufwand' },
  { value: 'Sonstiges', label: 'Sonstiges' },
];

const PRIORITY_OPTIONS = [
  { value: 'muss', label: 'Muss (verbindlich)' },
  { value: 'soll', label: 'Soll (wünschenswert)' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function PlanningRulesWizard({ open, onClose }: Props) {
  const [category, setCategory] = useState('Sonstiges');
  const [rule, setRule] = useState('');
  const [priority, setPriority] = useState('muss');
  const [existing, setExisting] = useState<PlanningRule[]>([]);
  const { status, error, submit, reset } = useWizardSubmit();

  const loadRules = () => {
    get<PlanningRule[]>('/planning-rules').then(setExisting).catch(() => {});
  };

  useEffect(() => {
    if (open) loadRules();
  }, [open]);

  const handleSubmit = async () => {
    if (!rule.trim()) return;
    const result = await submit('/planning-rules', {
      category,
      rule: rule.trim(),
      priority,
    });
    if (result) {
      setRule('');
      loadRules();
      setTimeout(() => reset(), 1500);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await del(`/planning-rules/${id}`);
      setExisting(prev => prev.filter(r => r.id !== id));
    } catch { /* ignore */ }
  };

  const handleClose = () => {
    setRule('');
    reset();
    onClose();
  };

  return (
    <WizardModal
      open={open}
      onClose={handleClose}
      title="Planungsregeln"
      footer={
        <WizardButton onClick={handleSubmit} loading={status === 'loading'} disabled={!rule.trim()}>
          Hinzufügen
        </WizardButton>
      }
    >
      {error && <WizardError message={error} />}
      {status === 'success' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 text-sm text-emerald-700">
          Regel gespeichert!
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <WizardLabel>Kategorie</WizardLabel>
          <WizardSelect value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />
        </div>
        <div>
          <WizardLabel>Priorität</WizardLabel>
          <WizardSelect value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
        </div>
      </div>

      <div>
        <WizardLabel>Regel</WizardLabel>
        <WizardInput
          value={rule}
          onChange={setRule}
          placeholder="z.B. Freitag abend max. 15 Min Kochzeit"
          autoFocus
        />
      </div>

      {/* Existing rules */}
      {existing.length > 0 && (
        <div>
          <WizardLabel>Aktuelle Regeln</WizardLabel>
          <div className="space-y-1 mt-1 max-h-48 overflow-y-auto">
            {existing.map(r => (
              <div key={r.id} className="flex items-center justify-between bg-gray-100 rounded-lg px-3 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      r.priority === 'muss' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {r.priority}
                    </span>
                    <span className="text-[10px] text-gray-400">{r.category}</span>
                  </div>
                  <p className="text-sm text-gray-700 truncate mt-0.5">{r.rule}</p>
                </div>
                <button
                  onClick={() => handleDelete(r.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors shrink-0 ml-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </WizardModal>
  );
}
